import { create } from 'zustand'
import type { PlatformStorage } from '../platform'

export interface Account {
  /** `${teamId}:${userId}` */
  id: string
  token: string
  teamId: string
  teamName: string
  teamDomain?: string
  teamIcon?: string
  userId: string
  userName?: string
}

interface AccountsState {
  accounts: Account[]
  activeId: string | null
  hydrated: boolean
  hydrate(storage: PlatformStorage): Promise<void>
  add(account: Account): void
  update(id: string, changes: Partial<Account>): void
  remove(id: string): void
  setActive(id: string): void
}

const KEY = 'assembly.accounts.v1'
let storage: PlatformStorage | null = null

export const useAccounts = create<AccountsState>((set, get) => {
  const persist = () => {
    const { accounts, activeId } = get()
    storage?.set(KEY, JSON.stringify({ accounts, activeId }))
  }
  return {
    accounts: [],
    activeId: null,
    hydrated: false,
    async hydrate(s) {
      storage = s
      try {
        const raw = await s.get(KEY)
        if (raw) {
          const saved = JSON.parse(raw) as { accounts: Account[]; activeId: string | null }
          set({ accounts: saved.accounts ?? [], activeId: saved.activeId ?? saved.accounts?.[0]?.id ?? null })
        }
      } finally {
        set({ hydrated: true })
      }
    },
    add(account) {
      set((s) => ({ accounts: [...s.accounts.filter((a) => a.id !== account.id), account], activeId: account.id }))
      persist()
    },
    update(id, changes) {
      set((s) => ({ accounts: s.accounts.map((a) => (a.id === id ? { ...a, ...changes } : a)) }))
      persist()
    },
    remove(id) {
      set((s) => {
        const accounts = s.accounts.filter((a) => a.id !== id)
        return { accounts, activeId: s.activeId === id ? (accounts[0]?.id ?? null) : s.activeId }
      })
      persist()
    },
    setActive(id) {
      set({ activeId: id })
      persist()
    },
  }
})

export const useActiveAccount = () => useAccounts((s) => s.accounts.find((a) => a.id === s.activeId) ?? null)
